import React, { useMemo } from "react";
import {
  useContests,
  useContestToMergedProblems,
  useRatingInfo,
  useMultipleUserSubmissions,
} from "../../api/APIClient";
import MergedProblem from "../../interfaces/MergedProblem";
import {
  constructContestStatusLabelMap,
  ContestId,
} from "../../interfaces/Status";
import { useLocalStorage } from "../../utils/LocalStorage";
import { ColorMode } from "../../utils/TableColor";
import {
  classifyContest,
  ContestCategory,
} from "../../utils/ContestClassifier";
import { TableTabButtons } from "./TableTab";
import { Options } from "./Options";
import { ContestTable } from "./ContestTable";
import { AtCoderRegularTable } from "./AtCoderRegularTable";

const HIDDEN_CONTEST_TYPES = new Set([
  "ADT",
  "ABC-Like",
  "ARC-Like",
  "AGC-Like",
]);

interface OuterProps {
  userId: string;
}

export const TablePage: React.FC<OuterProps> = (props) => {
  const [activeTab, setActiveTab] = useLocalStorage<ContestCategory>(
    "contestTableTab",
    "ABC"
  );
  const [
    showOnlyAttemptedContest,
    setShowOnlyAttemptedContest,
  ] = useLocalStorage("showOnlyAttemptedContest", false);
  const [hideCompleteContests, setHideCompleteContests] = useLocalStorage(
    "hideCompleteContests",
    false
  );
  const [showDifficulty, setShowDifficulty] = useLocalStorage(
    "showDifficulty",
    true
  );
  const colorMode = ColorMode.None;
  const showPenalties = false;
  const selectedLanguages = new Set<string>();
  const userRatingInfo = useRatingInfo(props.userId);
  const contestToProblems =
    useContestToMergedProblems() ?? new Map<ContestId, MergedProblem[]>();
  const { data: contests } = useContests();
  const submissions = useMultipleUserSubmissions([props.userId]).data ?? [];

  const statusLabelMap = constructContestStatusLabelMap(
    submissions,
    props.userId
  );

  const filteredContests = useMemo(() => {
    if (!contests) {
      return [];
    }
    return contests.filter((contest) => {
      const contestType = classifyContest(contest);
      if (HIDDEN_CONTEST_TYPES.has(contestType)) {
        return false;
      }
      return contestType === activeTab;
    });
  }, [contests, activeTab]);

  const AWC_STANDARD_PROBLEM_COUNT = 5;
  const isSpecialContest = (contestId: string): boolean =>
    activeTab === "AWC" &&
    (contestToProblems.get(contestId)?.length ?? 0) >
      AWC_STANDARD_PROBLEM_COUNT;
  const mainContests = filteredContests.filter((c) => !isSpecialContest(c.id));
  const specialContests = filteredContests.filter((c) =>
    isSpecialContest(c.id)
  );

  const regularTableTitle =
    activeTab === "ABC"
      ? "AtCoder Beginner Contest"
      : activeTab === "ARC"
      ? "AtCoder Regular Contest"
      : activeTab === "AGC"
      ? "AtCoder Grand Contest"
      : activeTab === "AWC"
      ? "AtCoder Weekday Contest"
      : activeTab === "PAST"
      ? "PAST"
      : `${activeTab} Contest`;

  return (
    <div className="mt-n4">
      <TableTabButtons active={activeTab} setActive={setActiveTab} />
      <Options
        showOnlyAttemptedContest={showOnlyAttemptedContest}
        toggleShowOnlyAttemptedContest={(): void =>
          setShowOnlyAttemptedContest(!showOnlyAttemptedContest)
        }
        hideCompleteContests={hideCompleteContests}
        toggleHideCompleteContests={(): void =>
          setHideCompleteContests(!hideCompleteContests)
        }
        showDifficulties={showDifficulty}
        toggleShowDifficulties={(): void => setShowDifficulty(!showDifficulty)}
      />
      {["ABC", "ARC", "AGC", "AWC", "PAST"].includes(activeTab) ? (
        <>
          <AtCoderRegularTable
            showDifficulty={showDifficulty}
            showOnlyAttemptedContest={showOnlyAttemptedContest}
            hideCompleteContests={hideCompleteContests}
            colorMode={colorMode}
            contests={mainContests}
            title={regularTableTitle}
            contestToProblems={contestToProblems}
            statusLabelMap={statusLabelMap}
            showPenalties={showPenalties}
            selectedLanguages={selectedLanguages}
            userRatingInfo={userRatingInfo}
          />
          {specialContests.length > 0 && (
            <ContestTable
              showDifficulty={showDifficulty}
              contests={specialContests}
              title="AtCoder Weekday Contest (Special)"
              contestToProblems={contestToProblems}
              showOnlyAttemptedContest={showOnlyAttemptedContest}
              hideCompleteContests={hideCompleteContests}
              colorMode={colorMode}
              statusLabelMap={statusLabelMap}
              showPenalties={showPenalties}
              selectedLanguages={selectedLanguages}
              userRatingInfo={userRatingInfo}
            />
          )}
        </>
      ) : (
        <ContestTable
          showDifficulty={showDifficulty}
          contests={filteredContests}
          title={activeTab}
          contestToProblems={contestToProblems}
          showOnlyAttemptedContest={showOnlyAttemptedContest}
          hideCompleteContests={hideCompleteContests}
          colorMode={colorMode}
          statusLabelMap={statusLabelMap}
          showPenalties={showPenalties}
          selectedLanguages={selectedLanguages}
          userRatingInfo={userRatingInfo}
        />
      )}
    </div>
  );
};
